import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../supabase';

const AuthContext = createContext();


export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userClinicId, setUserClinicId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRecovering, setIsRecovering] = useState(false);


  // Login function
  async function login(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;

    // Fetch user profile from public.users
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('*')
      .eq('id', data.user.id)
      .single();

    if (userError || !userData) {
      await supabase.auth.signOut();
      throw new Error('User profile not found. Please contact admin.');
    }

    if (userData.active === false) {
      await supabase.auth.signOut();
      throw new Error('Your account has been deactivated. Please contact admin.');
    }

    if (userData.clinic_id) {
      const { data: clinicData, error: clinicError } = await supabase
        .from('clinics')
        .select('active')
        .eq('id', userData.clinic_id)
        .single();

      if (!clinicError && clinicData && clinicData.active === false) {
        await supabase.auth.signOut();
        throw new Error('Your clinic has been deactivated. Please contact admin.');
      }
    }

    setUserRole(userData.role);
    setUserClinicId(userData.clinic_id);
    return userData;
  }

  // Logout function
  async function logout() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUserRole(null);
    setUserClinicId(null);
    setCurrentUser(null);
  }

  useEffect(() => {
    let subscription;
    let isMounted = true;

    // Step 1: Immediately read the session from localStorage
    supabase.auth.getSession()
      .then(async ({ data: { session } }) => {
        if (!isMounted) return;
        if (session?.user) {
          setCurrentUser(session.user);
          try {
            const { data: userData } = await supabase
              .from('users')
              .select('role, clinic_id, active')
              .eq('id', session.user.id)
              .single();
            if (userData && isMounted) {
              if (userData.active === false) {
                await supabase.auth.signOut();
                setCurrentUser(null);
                setUserRole(null);
                setUserClinicId(null);
                return;
              }

              if (userData.clinic_id) {
                const { data: clinicData } = await supabase
                  .from('clinics')
                  .select('active')
                  .eq('id', userData.clinic_id)
                  .single();

                if (clinicData && clinicData.active === false) {
                  await supabase.auth.signOut();
                  setCurrentUser(null);
                  setUserRole(null);
                  setUserClinicId(null);
                  return;
                }
              }

              setUserRole(userData.role);
              setUserClinicId(userData.clinic_id);
            }
          } catch (err) {
            console.error('Profile fetch error on restore:', err);
          }
        }
      })
      .catch(err => {
        console.error('getSession error:', err);
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);

        // Step 2: ONLY after getSession has resolved and released its lock, 
        // we subscribe to auth changes to prevent GoTrue "steal lock" race conditions.
    const { data } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            console.group('--- [Auth] State Change ---');
            console.log(`Event: ${event}`);
            console.log('User:', session?.user?.email || 'None');
            console.groupEnd();

            if (event === 'INITIAL_SESSION') return;
            if (!isMounted) return;

            if (session?.user) {
              setCurrentUser(session.user);
              // Defer database query to run outside of the auth state change lock context
              setTimeout(async () => {
                if (!isMounted) return;
                try {
                  const { data: userData } = await supabase
                    .from('users')
                    .select('role, clinic_id, active')
                    .eq('id', session.user.id)
                    .single();
                  if (userData && isMounted) {
                    if (userData.active === false) {
                      console.warn('[Auth] User account inactive, logging out');
                      await supabase.auth.signOut();
                      setCurrentUser(null);
                      setUserRole(null);
                      setUserClinicId(null);
                      return;
                    }

                    if (userData.clinic_id) {
                      const { data: clinicData } = await supabase
                        .from('clinics')
                        .select('active')
                        .eq('id', userData.clinic_id)
                        .single();

                      if (clinicData && clinicData.active === false) {
                        console.warn('[Auth] Clinic inactive, logging out');
                        await supabase.auth.signOut();
                        setCurrentUser(null);
                        setUserRole(null);
                        setUserClinicId(null);
                        return;
                      }
                    }

                    setUserRole(userData.role);
                    setUserClinicId(userData.clinic_id);
                  }
                } catch (err) {
                  console.error('[Auth] Profile fetch error:', err);
                }
              }, 0);
            } else {
              setCurrentUser(null);
              setUserRole(null);
              setUserClinicId(null);
            }
          }
        );
        subscription = data.subscription;
      });

    return () => {
      isMounted = false;
      if (subscription) subscription.unsubscribe();
    };
  }, []);

  // Step 5: Terminal Recovery Listener (Top Level)
  useEffect(() => {
    const handleRecovery = () => {
      setIsRecovering(true);
    };
    window.addEventListener('klinik-terminal-recovery', handleRecovery);
    return () => window.removeEventListener('klinik-terminal-recovery', handleRecovery);
  }, []);


  const value = {
    currentUser,
    userRole,
    userClinicId,
    loading,
    isRecovering,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}